import { useEffect, useMemo, useRef, useState } from "react";
import {
  decryptLinkArtifact,
  LinkIntegrityError,
  parseDownloadFragment,
  sha256Base64Url,
  unpackLinkEnvelope,
  type DecryptedLinkFile,
  type DownloadLinkCapability,
  type LinkServiceEnvironment,
} from "../../link-protocol";
import {
  createLinkApiClient,
  LinkApiError,
  type LinkApiClient,
  type LinkTransferProgress,
} from "./linkApiClient";
import {
  formatLinkBytes,
  formatLinkExpiry,
  safeDownloadName,
  saveVerifiedBlob,
} from "./uiUtils";
import { useLinkServiceStatus } from "./useLinkServiceStatus";
import "./linkShare.css";

export interface LinkDownloadPanelProps {
  fragment?: string;
  apiClient?: LinkApiClient;
  onVerified?: (files: readonly DecryptedLinkFile[]) => void;
}

type DownloadStep =
  "downloading" | "verifying-ciphertext" | "decrypting" | "verifying-files";

type DownloadState =
  | { kind: "consent"; capability: DownloadLinkCapability }
  | {
      kind: "working";
      capability: DownloadLinkCapability;
      step: DownloadStep;
      progress?: LinkTransferProgress;
    }
  | {
      kind: "verified";
      files: DecryptedLinkFile[];
      expiresAt: string;
      environment: LinkServiceEnvironment;
    }
  | { kind: "unavailable" }
  | { kind: "error"; message: string };

const downloadStepLabels: Readonly<Record<DownloadStep, string>> = {
  downloading: "Downloading encrypted file",
  "verifying-ciphertext": "Verifying encrypted download",
  decrypting: "Decrypting in this browser",
  "verifying-files": "Verifying files",
};

function initialState(fragment: string): DownloadState {
  try {
    return {
      kind: "consent",
      capability: parseDownloadFragment(fragment),
    };
  } catch {
    return { kind: "unavailable" };
  }
}

export function LinkDownloadPanel({
  fragment,
  apiClient,
  onVerified,
}: LinkDownloadPanelProps) {
  const currentFragment = fragment ?? window.location.hash;
  const [state, setState] = useState<DownloadState>(() =>
    initialState(currentFragment),
  );
  const [notice, setNotice] = useState("");
  const controller = useRef<AbortController | null>(null);
  const client = useMemo(() => apiClient ?? createLinkApiClient(), [apiClient]);
  const service = useLinkServiceStatus(client);

  useEffect(() => {
    controller.current?.abort();
    controller.current = null;
    const resetTimer = window.setTimeout(() => {
      setState(initialState(currentFragment));
      setNotice("");
    }, 0);
    return () => {
      window.clearTimeout(resetTimer);
    };
  }, [currentFragment]);

  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  async function handleDownload(capability: DownloadLinkCapability): Promise<void> {
    const abortController = new AbortController();
    controller.current = abortController;
    setNotice("");
    try {
      setState({
        kind: "working",
        capability,
        step: "downloading",
        progress: { loaded: 0, total: 1 },
      });
      const downloaded = await client.downloadEncryptedArtifact({
        accessToken: capability.accessToken,
        signal: abortController.signal,
        onProgress(progress) {
          setState({
            kind: "working",
            capability,
            step: "downloading",
            progress,
          });
        },
      });

      setState({
        kind: "working",
        capability,
        step: "verifying-ciphertext",
      });
      if (
        (await sha256Base64Url(downloaded.artifact)) !== downloaded.ciphertextSha256
      ) {
        throw new LinkIntegrityError("The encrypted download digest did not match.");
      }

      setState({
        kind: "working",
        capability,
        step: "decrypting",
      });
      const envelope = await decryptLinkArtifact(
        downloaded.artifact,
        capability.decryptionKey,
      );

      // AES-GCM authentication completed before this point; unpacking performs
      // the remaining manifest and per-file digest validation.
      setState({
        kind: "working",
        capability,
        step: "verifying-files",
      });
      const files = await unpackLinkEnvelope(envelope);
      setState({
        kind: "verified",
        files,
        expiresAt: downloaded.expiresAt,
        environment: downloaded.environment,
      });
      onVerified?.(files);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setState({ kind: "consent", capability });
        setNotice("Download cancelled.");
      } else if (
        error instanceof LinkApiError &&
        error.code === "expired_or_unavailable"
      ) {
        setState({ kind: "unavailable" });
      } else if (error instanceof LinkIntegrityError) {
        setState({
          kind: "error",
          message: "Integrity or decryption verification failed. No file was exposed.",
        });
      } else {
        setState({
          kind: "error",
          message:
            error instanceof Error
              ? error.message
              : "The encrypted file could not be downloaded.",
        });
      }
    } finally {
      controller.current = null;
    }
  }

  async function handleShare(files: readonly DecryptedLinkFile[]) {
    if (typeof navigator.share !== "function") {
      setNotice("System sharing is not available. Save each verified file.");
      return;
    }
    const shareFiles = files.map(
      (file) =>
        new File([file.blob], safeDownloadName(file.name), {
          type: file.type || "application/octet-stream",
        }),
    );
    if (
      typeof navigator.canShare === "function" &&
      !navigator.canShare({ files: shareFiles })
    ) {
      setNotice("This browser cannot share these files. Save them instead.");
      return;
    }
    try {
      await navigator.share({ files: shareFiles });
      setNotice("Share sheet opened.");
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setNotice("The browser could not share the verified files.");
      }
    }
  }

  return (
    <section className="link-panel" aria-labelledby="link-download-title">
      <div className="link-panel__heading">
        <p className="link-panel__eyebrow">Private bearer link</p>
        <h2 id="link-download-title">Download shared file</h2>
      </div>

      {service.status?.environment === "local-simulation" && (
        <div className="link-banner link-banner--warning" role="status">
          Local development simulation. The encrypted artifact is held only in this
          local server process and is not stored in Cloudflare.
        </div>
      )}

      {service.status === null && (
        <div
          className={`link-banner${
            service.error === null ? "" : " link-banner--warning"
          }`}
          role="status"
        >
          {service.error ?? "Checking the temporary link service environment."}
        </div>
      )}

      {state.kind === "consent" && (
        <div className="link-disclosure">
          <span className="link-status-chip">Encrypted link</span>
          <h3>Download and decrypt on this device?</h3>
          <p>
            The encrypted file will be downloaded from temporary storage, then
            authenticated and decrypted only in this browser. Saving or sharing happens
            after verification.
          </p>
          <button
            className="link-button link-button--primary"
            type="button"
            onClick={() => {
              void handleDownload(state.capability);
            }}
          >
            Download and decrypt
          </button>
        </div>
      )}

      {state.kind === "working" && (
        <div className="link-progress" aria-live="polite">
          <span className="link-status-chip">{downloadStepLabels[state.step]}</span>
          <h3>{downloadStepLabels[state.step]}</h3>
          {state.progress !== undefined && (
            <>
              <progress value={state.progress.loaded} max={state.progress.total} />
              <p>
                {formatLinkBytes(state.progress.loaded)} of{" "}
                {formatLinkBytes(state.progress.total)}
              </p>
            </>
          )}
          <button
            className="link-button link-button--secondary"
            type="button"
            onClick={() => {
              controller.current?.abort();
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {state.kind === "verified" && (
        <div className="link-ready" aria-live="polite">
          {state.environment === "local-simulation" && (
            <div className="link-banner link-banner--warning" role="status">
              This file came from the local development simulation.
            </div>
          )}
          <span className="link-status-chip link-status-chip--success">
            {state.files.length === 1 ? "File verified" : "Files verified"}
          </span>
          <h3>Safe to save or share</h3>
          <p>
            Ciphertext authentication, the encrypted manifest, and every original file
            digest passed verification.
          </p>
          <p className="link-expiry">
            Link available until{" "}
            <time dateTime={state.expiresAt}>{formatLinkExpiry(state.expiresAt)}</time>
          </p>
          <ul className="link-verified-files">
            {state.files.map((file) => (
              <li key={`${file.name}:${file.sha256}`}>
                <span>
                  <strong>{file.name}</strong>
                  <small>{formatLinkBytes(file.blob.size)}</small>
                </span>
                <button
                  className="link-button link-button--secondary"
                  type="button"
                  onClick={() => {
                    saveVerifiedBlob(file.name, file.blob);
                    setNotice("Save started for the verified file.");
                  }}
                >
                  Save file
                </button>
              </li>
            ))}
          </ul>
          <button
            className="link-button link-button--primary"
            type="button"
            onClick={() => {
              void handleShare(state.files);
            }}
          >
            Share
          </button>
        </div>
      )}

      {state.kind === "unavailable" && (
        <div className="link-error" role="alert">
          <span className="link-status-chip link-status-chip--danger">
            Link expired or unavailable
          </span>
          <h3>This link cannot be opened</h3>
          <p>
            It may have expired, been revoked, or no longer exist. Ask the sender to
            create a new link.
          </p>
        </div>
      )}

      {state.kind === "error" && (
        <div className="link-error" role="alert">
          <span className="link-status-chip link-status-chip--danger">
            Needs attention
          </span>
          <h3>Download failed</h3>
          <p>{state.message}</p>
        </div>
      )}

      {notice && (
        <p className="link-notice" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}
